(function (global) {
  'use strict';

  const PATTERNS = Object.freeze({
    'catch-pee': [[670, 0, 0.09], [860, 0.08, 0.1]],
    'catch-poop': [[390, 0, 0.1], [560, 0.09, 0.12]],
    miss: [[250, 0, 0.16]],
    milestone: [[650, 0, 0.08], [810, 0.09, 0.08], [990, 0.18, 0.12]],
    'game-over': [[430, 0, 0.1], [330, 0.12, 0.1], [250, 0.24, 0.17]],
    menu: [[550, 0, 0.065]]
  });

  function createAudioManager({ audioContextFactory, audioFactory, effectSources = {}, voiceSources = {}, clock = Date.now } = {}) {
    let context = null;
    let activeVoice = null;
    let lastVoiceAt = -Infinity;
    let lastEffectAt = -Infinity;
    let destroyed = false;

    function unlock() {
      if (destroyed) return false;
      if (!context && typeof audioContextFactory === 'function') {
        try { context = audioContextFactory(); } catch {}
      }
      try { context?.resume?.().catch?.(() => {}); } catch {}
      return Boolean(context);
    }

    function synthesize(kind) {
      if (!context || !PATTERNS[kind] || destroyed) return false;
      try {
        const start = context.currentTime;
        for (const [frequency, offset, duration] of PATTERNS[kind]) {
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          oscillator.type = kind === 'miss' || kind === 'game-over' ? 'triangle' : 'sine';
          oscillator.frequency.setValueAtTime(frequency, start + offset);
          gain.gain.setValueAtTime(0.001, start + offset);
          gain.gain.exponentialRampToValueAtTime(0.027, start + offset + 0.012);
          gain.gain.exponentialRampToValueAtTime(0.001, start + offset + duration);
          oscillator.connect(gain).connect(context.destination);
          oscillator.onended = () => { try { oscillator.disconnect(); gain.disconnect(); } catch {} };
          oscillator.start(start + offset);
          oscillator.stop(start + offset + duration + 0.01);
        }
        return true;
      } catch { return false; }
    }

    function playEffect(kind, enabled = true) {
      if (!enabled || destroyed || !PATTERNS[kind]) return false;
      const time = clock();
      if (kind !== 'game-over' && kind !== 'milestone' && time - lastEffectAt < 90) return false;
      lastEffectAt = time;
      const source = effectSources[kind];
      if (typeof source !== 'string' || !source || typeof audioFactory !== 'function') return synthesize(kind);
      try {
        const clip = audioFactory(source);
        clip.volume = 0.28;
        let fellBack = false;
        const fallback = () => { if (!fellBack) { fellBack = true; synthesize(kind); } };
        clip.addEventListener?.('error', fallback, { once: true });
        const started = clip.play();
        started?.catch?.(fallback);
        return true;
      } catch { return synthesize(kind); }
    }

    function stopVoice() {
      if (!activeVoice) return;
      try { activeVoice.pause(); activeVoice.currentTime = 0; } catch {}
      activeVoice = null;
    }

    function playVoice(mood, enabled = true) {
      if (!enabled || destroyed || typeof audioFactory !== 'function') return false;
      const sources = voiceSources[mood];
      if (!Array.isArray(sources) || !sources.length) return false;
      const time = clock();
      if (time - lastVoiceAt < 900) return false;
      const source = sources.find(value => typeof value === 'string' && value);
      if (!source) return false;
      stopVoice();
      try {
        const clip = audioFactory(source);
        clip.volume = 0.5;
        clip.addEventListener?.('ended', () => { if (activeVoice === clip) activeVoice = null; }, { once: true });
        clip.addEventListener?.('error', () => { if (activeVoice === clip) activeVoice = null; }, { once: true });
        activeVoice = clip;
        lastVoiceAt = time;
        clip.play()?.catch?.(() => { if (activeVoice === clip) activeVoice = null; });
        return true;
      } catch { activeVoice = null; return false; }
    }

    function suspend() { stopVoice(); try { context?.suspend?.().catch?.(() => {}); } catch {} }
    function resume() { try { context?.resume?.().catch?.(() => {}); } catch {} }
    function destroy() {
      destroyed = true;
      stopVoice();
      try { context?.close?.().catch?.(() => {}); } catch {}
      context = null;
    }

    return Object.freeze({ unlock, playEffect, playVoice, stopVoice, suspend, resume, destroy });
  }

  const api = Object.freeze({ PATTERNS, createAudioManager });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global) global.BBC_DIAPER_DASH_AUDIO = api;
})(typeof window !== 'undefined' ? window : null);
